import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "react-oidc-context";
import { Sidebar } from "../../components/Sidebar";
import { PageHeader, LoadingState, ErrorState } from "../../components/UI";

function GreenMilesRoomNumberManager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingRooms, setSavingRooms] = useState({});
  const [saveMessages, setSaveMessages] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [editingRooms, setEditingRooms] = useState({});

  const auth = useAuth();
  const token = auth.user.access_token;
  const API_URL = process.env.REACT_APP_API_URL || "/api";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Récupération des détails de l'hôtel GreenMiles
        const response = await axios.get(`${API_URL}/greenmiles/hotels/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success === "OK") {
          const hotelData = response.data.result;
          setHotel(hotelData);

          // Les chambres sont dans hotelData.rooms
          const sortedRooms = (hotelData.rooms || []).sort((a, b) => a.rRoomNumber.localeCompare(b.rRoomNumber, undefined, { numeric: true }));
          setRooms(sortedRooms);
        } else {
          throw new Error(response.data.message || "Erreur lors du chargement des données");
        }
      } catch (err) {
        console.error("Error fetching hotel data:", err);
        setError("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, API_URL, token]);

  // Pour initialiser le champ d'édition sans affecter l'affichage
  useEffect(() => {
    if (rooms.length > 0) {
      // Initialiser editingRooms avec les valeurs actuelles
      const initialEditingState = {};
      rooms.forEach((room) => {
        initialEditingState[room.rId] = room.rRoomNumber;
      });
      setEditingRooms(initialEditingState);
    }
  }, [rooms]);

  const handleRoomNumberChange = (roomId, newNumber) => {
    // Empêcher la saisie d'une chaîne vide
    if (newNumber.trim() === "") {
      // On peut quand même mettre à jour le champ pour permettre à l'utilisateur
      // d'effacer et recommencer la saisie, mais il ne pourra pas sauvegarder
      setEditingRooms((prev) => ({ ...prev, [roomId]: newNumber }));

      // Optionnel: afficher un message d'avertissement
      setSaveMessages((prev) => ({
        ...prev,
        [roomId]: "Le numéro ne peut pas être vide",
      }));
    } else {
      // Effacer le message d'erreur si présent
      if (saveMessages[roomId] && saveMessages[roomId].includes("vide")) {
        setSaveMessages((prev) => {
          const newMessages = { ...prev };
          delete newMessages[roomId];
          return newMessages;
        });
      }

      setEditingRooms((prev) => ({ ...prev, [roomId]: newNumber }));
    }
  };

  const handleSaveRoom = async (roomId) => {
    setSavingRooms((prev) => ({ ...prev, [roomId]: true }));
    try {
      const newNumber = editingRooms[roomId];
      const currentRoom = rooms.find((room) => room.rId === roomId);

      // Vérifier si le numéro est vide
      if (!newNumber || newNumber.trim() === "") {
        setSaveMessages((prev) => ({
          ...prev,
          [roomId]: "Le numéro de chambre ne peut pas être vide",
        }));
        setTimeout(() => {
          setSaveMessages((prev) => {
            const newMessages = { ...prev };
            delete newMessages[roomId];
            return newMessages;
          });
        }, 3000);

        // Restaurer l'ancien numéro
        setEditingRooms((prev) => ({
          ...prev,
          [roomId]: currentRoom.rRoomNumber,
        }));

        setSavingRooms((prev) => ({ ...prev, [roomId]: false }));
        return;
      }

      // Vérifier si le numéro a réellement changé
      if (newNumber === currentRoom.rRoomNumber) {
        setSaveMessages((prev) => ({
          ...prev,
          [roomId]: "Aucun changement détecté",
        }));
        setTimeout(() => {
          setSaveMessages((prev) => {
            const newMessages = { ...prev };
            delete newMessages[roomId];
            return newMessages;
          });
        }, 3000);
        setSavingRooms((prev) => ({ ...prev, [roomId]: false }));
        return;
      }

      // Appel API pour GreenMiles
      const response = await axios.patch(`${API_URL}/greenmiles/hotels/${id}/rooms/${roomId}`, { roomNumber: newNumber }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.success) {
        // Mettre à jour la liste des chambres avec le nouveau numéro
        setRooms((prevRooms) => {
          const updatedRooms = prevRooms.map((room) => (room.rId === roomId ? { ...room, rRoomNumber: newNumber } : room));
          // Re-trier les chambres après la mise à jour
          return updatedRooms.sort((a, b) => a.rRoomNumber.localeCompare(b.rRoomNumber, undefined, { numeric: true }));
        });

        setSaveMessages((prev) => ({
          ...prev,
          [roomId]: "Modification sauvegardée avec succès !",
        }));
        setTimeout(() => {
          setSaveMessages((prev) => {
            const newMessages = { ...prev };
            delete newMessages[roomId];
            return newMessages;
          });
        }, 3000);

        // Mettre à jour l'état d'édition avec la nouvelle valeur
        setEditingRooms((prev) => ({
          ...prev,
          [roomId]: newNumber,
        }));
      } else {
        throw new Error(response.data.message || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Error saving room:", error);
      const errorMessage = error.response?.data?.message || error.message || "Erreur lors de la sauvegarde";
      setSaveMessages((prev) => ({
        ...prev,
        [roomId]: errorMessage,
      }));
      setTimeout(() => {
        setSaveMessages((prev) => {
          const newMessages = { ...prev };
          delete newMessages[roomId];
          return newMessages;
        });
      }, 5000);
    } finally {
      setSavingRooms((prev) => ({ ...prev, [roomId]: false }));
    }
  };

  const filteredRooms = rooms.filter((room) => {
    const matchesSearch = room.rRoomNumber.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleGoBack = () => {
    navigate(`/hotels/${id}/greenmiles`);
  };

  if (loading) {
    return <LoadingState message="Chargement des données..." />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-6 overflow-auto">
        <PageHeader title={`Gestion des numéros de chambres - ${hotel?.hName}`} onGoBack={handleGoBack} />

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher une chambre</label>
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Tapez le numéro de chambre..." />
          </div>

          {/* Statistiques des chambres */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm font-medium text-blue-600">Total chambres</div>
              <div className="text-2xl font-bold text-blue-900">{rooms.length}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm font-medium text-green-600">Avec QR code</div>
              <div className="text-2xl font-bold text-green-900">{rooms.filter((room) => room.has_qr_code === 1).length}</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="text-sm font-medium text-orange-600">Sans QR code</div>
              <div className="text-2xl font-bold text-orange-900">{rooms.filter((room) => room.has_qr_code === 0).length}</div>
            </div>
          </div>

          {filteredRooms.length === 0 ? (
            <div className="text-center py-8 text-gray-500">{searchTerm ? "Aucune chambre trouvée avec ce numéro" : "Aucune chambre disponible"}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Numéro actuel</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tag</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">QR Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nouveau numéro</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRooms.map((room) => (
                    <tr key={room.rId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{room.rRoomNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${room.rTag === "Expérience" ? "bg-green-100 text-green-800" : room.rTag === "Essentielle" ? "bg-blue-100 text-blue-800" : room.rTag === "Témoin" ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-800"}`}>{room.rTag || "Aucun"}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{room.has_qr_code === 1 ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">✓ QR Code</span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">⚠ Sans QR</span>}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input type="text" value={editingRooms[room.rId] !== undefined ? editingRooms[room.rId] : ""} onChange={(e) => handleRoomNumberChange(room.rId, e.target.value)} className="border border-gray-300 rounded-md px-3 py-2 w-40 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Nouveau numéro" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap relative">
                        <button onClick={() => handleSaveRoom(room.rId)} disabled={savingRooms[room.rId] || !editingRooms[room.rId] || editingRooms[room.rId].trim() === ""} className={`px-4 py-2 rounded-md text-white text-sm font-medium transition-colors duration-200 ${savingRooms[room.rId] || !editingRooms[room.rId] || editingRooms[room.rId].trim() === "" ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}>
                          {savingRooms[room.rId] ? "Enregistrement..." : "Enregistrer"}
                        </button>
                        {saveMessages[room.rId] && <div className={`absolute left-0 top-full mt-1 text-sm font-medium ${saveMessages[room.rId].includes("succès") ? "text-green-600" : "text-red-600"}`}>{saveMessages[room.rId]}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GreenMilesRoomNumberManager;
